import { useState, useEffect, useCallback, useRef } from "react";
import { apiClient } from "../../../api/apiServices";

// ── Helpers ───────────────────────────────────────────────────
const fmt     = (n) => Number(n || 0).toLocaleString("en-NG");
const fmtDate = (d) => d ? new Date(d).toLocaleString("en-NG", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

const STATUS_STYLE = {
  draft:      { color: "#64748B", bg: "#F8FAFC",   label: "Draft"    },
  queued:     { color: "#0284C7", bg: "#EFF6FF",   label: "Queued"   },
  sending:    { color: "#D97706", bg: "#FFFBEB",   label: "Sending…" },
  sent:       { color: "#059669", bg: "#ECFDF5",   label: "Sent"     },
  failed:     { color: "#DC2626", bg: "#FEF2F2",   label: "Failed"   },
  cancelled:  { color: "#64748B", bg: "#F8FAFC",   label: "Cancelled"},
};

const RECIPIENT_TYPES = [
  { value: "all_users",    label: "All Users",          icon: "ri-group-line",           desc: "Everyone with an account" },
  { value: "kyc_approved", label: "KYC Approved",       icon: "ri-shield-check-line",    desc: "Verified users only" },
  { value: "kyc_pending",  label: "No KYC / Pending",   icon: "ri-shield-line",          desc: "Users yet to verify" },
  { value: "active_30d",   label: "Active (30d)",        icon: "ri-user-star-line",       desc: "Transacted in last 30 days" },
  { value: "inactive_30d", label: "Inactive (30d)",      icon: "ri-user-unfollow-line",   desc: "No transaction in 30 days" },
  { value: "imported_list",label: "Imported List",       icon: "ri-file-list-3-line",     desc: "Upload CSV or paste list" },
];

// ── Parse CSV / paste for import ──────────────────────────────
function parseImportedList(raw, type) {
  const lines = raw.split(/[\n,;]+/).map(s => s.trim()).filter(Boolean);
  const valid = [], invalid = [];
  lines.forEach(line => {
    // Remove quotes/spaces
    const clean = line.replace(/["'\s]/g, "");
    if (type === "email") {
      const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean);
      isEmail ? valid.push({ email: clean }) : invalid.push(clean);
    } else {
      // Accept 11-digit (0XXX) or +234/234 prefixed numbers
      const isPhone = /^(\+?234|0)\d{10}$/.test(clean);
      isPhone ? valid.push({ phone: clean }) : invalid.push(clean);
    }
  });
  return { valid, invalid };
}

// ── Campaign status pill ───────────────────────────────────────
function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.draft;
  return (
    <span className="text-[10px] font-bold px-2.5 py-1 rounded-full capitalize"
      style={{ background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

// ── Scrollable recipient list with remove ─────────────────────
function RecipientList({ items, onRemove, type, loading }) {
  const [search, setSearch] = useState("");

  const filtered = items.filter(r =>
    !search.trim() || r.identifier.toLowerCase().includes(search.toLowerCase()) ||
    (r.userName || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="bg-white/3 rounded-xl border border-white/8 overflow-hidden">
      {/* Search + count */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-white/5">
        <i className="ri-search-line text-white/25 text-xs" />
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder={`Search ${type === "email" ? "emails" : "phones"}…`}
          className="flex-1 bg-transparent text-xs text-white placeholder-white/20 focus:outline-none" />
        <span className="text-white/25 text-[10px] flex-shrink-0">
          {filtered.length}/{items.length}
        </span>
      </div>

      {/* Scrollable list */}
      <div className="overflow-y-auto max-h-52 divide-y divide-white/4">
        {loading ? (
          [...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center gap-3 px-3 py-2.5">
              <div className="flex-1 h-3 bg-white/5 rounded animate-pulse" />
              <div className="w-4 h-4 bg-white/5 rounded animate-pulse" />
            </div>
          ))
        ) : filtered.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <p className="text-white/20 text-xs">
              {search.trim() ? "No matches" : "No recipients yet"}
            </p>
          </div>
        ) : filtered.map((r, i) => (
          <div key={i} className="flex items-center gap-3 px-3 py-2 group hover:bg-white/3 transition-colors">
            {/* Avatar letter */}
            <div className="w-6 h-6 rounded-full bg-[#00C896]/10 flex items-center justify-center flex-shrink-0">
              <span className="text-[#00C896] text-[9px] font-bold">
                {(r.userName || r.identifier || "?")[0].toUpperCase()}
              </span>
            </div>

            {/* Identifier + name */}
            <div className="flex-1 min-w-0">
              <p className="text-white/70 text-xs truncate font-mono">{r.identifier}</p>
              {r.userName && (
                <p className="text-white/25 text-[10px] truncate">{r.userName}</p>
              )}
            </div>

            {/* Status badge (for already-stored recipients) */}
            {r.status && r.status !== "pending" && (
              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                r.status === "sent"   ? "bg-[#00C896]/15 text-[#00C896]"
                : r.status === "failed" ? "bg-red-500/15 text-red-400"
                : "bg-white/5 text-white/30"
              }`}>{r.status}</span>
            )}

            {/* Remove button */}
            {onRemove && (
              <button onClick={() => onRemove(r.identifier)}
                className="w-5 h-5 rounded flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500/20 flex-shrink-0">
                <i className="ri-close-line text-red-400/70 text-[10px]" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Compose / Edit Modal (with recipient preview + remove)
// ─────────────────────────────────────────────────────────────
function ComposeModal({ campaign, onClose, onSaved }) {
  const isEdit = !!campaign?._id;
  const [form, setForm] = useState({
    title:         campaign?.title         || "",
    type:          campaign?.type          || "email",
    subject:       campaign?.subject       || "",
    body:          campaign?.body          || "",
    fromName:      campaign?.fromName      || "Easwallet",
    recipientType: campaign?.recipientType || "all_users",
  });
  const [importRaw, setImportRaw]           = useState("");
  const [importResult, setImportResult]     = useState(null);  // { valid, invalid }
  const [editableList, setEditableList]     = useState([]);    // for imported_list, after parse
  const [previewList, setPreviewList]       = useState([]);    // for DB segments, loaded on demand
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showPreview, setShowPreview]       = useState(false);
  const [previewTotal, setPreviewTotal]     = useState(null);  // full count from server
  const [excludedIds, setExcludedIds]       = useState([]);    // identifiers removed from DB segments
  const [recipientCount, setRecipientCount] = useState(null);
  const [countLoading, setCountLoading]     = useState(false);
  const [saving, setSaving]                 = useState(false);
  const [error, setError]                   = useState("");
  const [sendNow, setSendNow]               = useState(false);
  const fileRef   = useRef();
  const countTimer = useRef(null);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // ── Live recipient count ────────────────────────────────────
  useEffect(() => {
    if (form.recipientType === "imported_list") { setRecipientCount(null); return; }
    clearTimeout(countTimer.current);
    countTimer.current = setTimeout(async () => {
      setCountLoading(true);
      setPreviewList([]);
      setShowPreview(false);
      setExcludedIds([]);
      setPreviewTotal(null);
      try {
        const res = await apiClient.get(
          `/mtrade/admin/marketing/preview-count?recipientType=${form.recipientType}&type=${form.type}`
        );
        if (res.data.status) setRecipientCount(res.data.count);
      } catch { /* silent */ }
      finally { setCountLoading(false); }
    }, 400);
  }, [form.recipientType, form.type]);

  // ── Parse import ────────────────────────────────────────────
  useEffect(() => {
    if (form.recipientType !== "imported_list" || !importRaw.trim()) {
      setImportResult(null);
      setEditableList([]);
      return;
    }
    const result = parseImportedList(importRaw, form.type);
    setImportResult(result);
    setEditableList(result.valid.map(r => ({
      identifier: r.email || r.phone,
      status: "pending",
    })));
  }, [importRaw, form.type]);

  // ── Load DB segment preview (works for both new + saved campaigns) ──
  const loadPreview = async () => {
    if (form.recipientType === "imported_list") return;
    setPreviewLoading(true);
    setShowPreview(true);
    try {
      let data, total;
      if (isEdit) {
        // Use existing campaign's saved segment
        const res = await apiClient.get(
          `/mtrade/admin/marketing/campaigns/${campaign._id}/preview-recipients`
        );
        data  = res.data.data;
        total = res.data.total;
      } else {
        // Resolve segment for an unsaved campaign (POST endpoint)
        const res = await apiClient.post("/mtrade/admin/marketing/preview-recipients", {
          recipientType: form.recipientType,
          type:          form.type,
        });
        data  = res.data.data;
        total = res.data.total;
      }
      // Apply already-excluded identifiers
      const excSet = new Set(excludedIds.map(e => e.toLowerCase()));
      setPreviewList((data || []).filter(r => !excSet.has(r.identifier.toLowerCase())));
      setPreviewTotal(total || 0);
    } catch { /* silent */ }
    finally { setPreviewLoading(false); }
  };

  // ── Remove from imported list ─────────────────────────────
  const handleRemoveImported = (identifier) => {
    setEditableList(prev => prev.filter(r => r.identifier !== identifier));
  };

  // ── Remove from DB-segment preview — tracks an exclude list ──
  const handleRemovePreview = (identifier) => {
    setPreviewList(prev => prev.filter(r => r.identifier !== identifier));
    setExcludedIds(prev => [...prev, identifier]);
    // Update the live count
    setRecipientCount(prev => prev !== null ? Math.max(0, prev - 1) : null);
  };

  // ── Restore all removals ──────────────────────────────────
  const handleRestoreAll = async () => {
    setExcludedIds([]);
    setShowPreview(false);
    // Reload the full list without exclusions
    await loadPreview();
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setImportRaw(ev.target.result);
    reader.readAsText(file);
    e.target.value = "";
  };

  const insertVar = (v) => {
    const ta = document.getElementById("msg-body");
    if (!ta) { set("body", form.body + `{{${v}}}`); return; }
    const start = ta.selectionStart, end = ta.selectionEnd;
    set("body", form.body.slice(0, start) + `{{${v}}}` + form.body.slice(end));
    setTimeout(() => { ta.selectionStart = ta.selectionEnd = start + v.length + 4; ta.focus(); }, 0);
  };

  const handleSave = async (send = false) => {
    setError("");
    if (!form.title.trim())                                  { setError("Campaign title required"); return; }
    if (!form.body.trim())                                   { setError("Message body required"); return; }
    if (form.type === "email" && !form.subject.trim())       { setError("Subject required for email"); return; }
    if (form.recipientType === "imported_list" && editableList.length === 0) {
      setError("No valid recipients in your list"); return;
    }
    setSaving(true);
    try {
    const payload = {
        ...form,
        sendNow: send,
        importedList: form.recipientType === "imported_list"
          ? editableList.map(r => ({
              [form.type === "email" ? "email" : "phone"]: r.identifier,
            }))
          : [],
        excludeList: form.recipientType !== "imported_list" ? excludedIds : [],
      };
      if (isEdit) {
        await apiClient.patch(`/mtrade/admin/marketing/campaigns/${campaign._id}`, payload);
        if (send) await apiClient.post(`/mtrade/admin/marketing/campaigns/${campaign._id}/send`);
      } else {
        await apiClient.post("/mtrade/admin/marketing/campaigns", payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || "Save failed");
    } finally { setSaving(false); }
  };

  const totalRecipients = form.recipientType === "imported_list"
    ? editableList.length
    : recipientCount !== null
      ? Math.max(0, recipientCount - excludedIds.length)
      : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
      <div className="bg-[#0f0f0f] rounded-2xl border border-white/10 w-full max-w-2xl shadow-2xl flex flex-col max-h-[92vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 flex-shrink-0">
          <p className="text-white font-bold">{isEdit ? "Edit Campaign" : "New Campaign"}</p>
          <button onClick={onClose} className="w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center hover:bg-white/10">
            <i className="ri-close-line text-white/50 text-xs" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {error && (
            <div className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-xs">
              <i className="ri-error-warning-line" /> {error}
            </div>
          )}

          {/* Title + type */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Campaign Name</label>
              <input value={form.title} onChange={e => set("title", e.target.value)}
                placeholder="e.g. June Promo Blast"
                className="w-full bg-[#161616] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40" />
            </div>
            <div>
              <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Channel</label>
              <div className="flex gap-2">
                {["email", "sms"].map(t => (
                  <button key={t} onClick={() => set("type", t)}
                    className={`flex-1 py-2.5 rounded-xl text-xs font-bold capitalize border transition-all ${
                      form.type === t
                        ? t === "email" ? "bg-[#00C896]/20 text-[#00C896] border-[#00C896]/30" : "bg-blue-500/20 text-blue-400 border-blue-500/30"
                        : "bg-white/5 text-white/30 border-white/8"
                    }`}>
                    <i className={`${t === "email" ? "ri-mail-line" : "ri-message-2-line"} mr-1.5`} />{t.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* From + subject (email) */}
          {form.type === "email" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">From Name</label>
                <input value={form.fromName} onChange={e => set("fromName", e.target.value)}
                  placeholder="Easwallet"
                  className="w-full bg-[#161616] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40" />
              </div>
              <div>
                <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Subject Line</label>
                <input value={form.subject} onChange={e => set("subject", e.target.value)}
                  placeholder="e.g. 🎁 Special offer for you"
                  className="w-full bg-[#161616] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40" />
              </div>
            </div>
          )}

          {/* Body */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-white/40 text-[10px] uppercase tracking-wide">Message</label>
              <div className="flex gap-1 flex-wrap justify-end">
                {["firstName", "lastName", "email", "referralCode"].map(v => (
                  <button key={v} onClick={() => insertVar(v)}
                    className="text-[10px] px-2 py-0.5 bg-white/5 hover:bg-[#00C896]/15 text-white/40 hover:text-[#00C896] rounded-lg transition-colors font-mono">
                    {`{{${v}}}`}
                  </button>
                ))}
              </div>
            </div>
            <textarea
              id="msg-body"
              value={form.body}
              onChange={e => set("body", e.target.value)}
              rows={form.type === "sms" ? 4 : 8}
              placeholder={form.type === "sms"
                ? "Hi {{firstName}}, great news from Easwallet!…"
                : "Hi {{firstName}},\n\nWe have an exciting update for you…"}
              className="w-full bg-[#161616] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40 resize-none font-mono leading-relaxed"
            />
            <div className="flex items-center justify-between mt-1">
              <p className="text-white/20 text-[10px]">Click {"{{var}}"} buttons to insert at cursor</p>
              {form.type === "sms" && (
                <p className={`text-[10px] font-semibold ${form.body.length > 160 ? "text-amber-400" : "text-white/25"}`}>
                  {form.body.length} chars {form.body.length > 160 ? `(${Math.ceil(form.body.length / 160)} SMS)` : ""}
                </p>
              )}
            </div>
          </div>

          {/* Recipient type */}
          <div>
            <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-2">Recipients</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {RECIPIENT_TYPES.map(rt => (
                <button key={rt.value} onClick={() => set("recipientType", rt.value)}
                  className={`p-3 rounded-xl border text-left transition-all ${
                    form.recipientType === rt.value
                      ? "border-[#00C896]/40 bg-[#00C896]/10"
                      : "border-white/8 bg-white/4 hover:bg-white/8"
                  }`}>
                  <i className={`${rt.icon} text-base mb-1 block ${form.recipientType === rt.value ? "text-[#00C896]" : "text-white/40"}`} />
                  <p className={`text-xs font-semibold ${form.recipientType === rt.value ? "text-[#00C896]" : "text-white/60"}`}>{rt.label}</p>
                  <p className="text-white/25 text-[10px] mt-0.5 leading-tight">{rt.desc}</p>
                </button>
              ))}
            </div>

            {/* Count row + preview toggle for DB segments */}
            {form.recipientType !== "imported_list" && (
              <div className="mt-2 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <i className="ri-group-line text-white/30 text-xs" />
                    <p className="text-white/40 text-xs">
                      {countLoading ? "Counting…" : totalRecipients !== null
                        ? <span className="text-[#00C896] font-bold">{fmt(totalRecipients)}</span>
                        : "—"}{" "}
                      {form.type === "email" ? "users with email" : "users with phone"}
                      {excludedIds.length > 0 && (
                        <span className="text-red-400/70 ml-1.5">
                          ({excludedIds.length} excluded)
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {excludedIds.length > 0 && (
                      <button onClick={handleRestoreAll}
                        className="text-[10px] text-white/30 hover:text-white/60 transition-colors flex items-center gap-1">
                        <i className="ri-restart-line" /> Restore all
                      </button>
                    )}
                    <button onClick={showPreview ? () => setShowPreview(false) : loadPreview}
                      className="text-[10px] text-[#00C896]/70 hover:text-[#00C896] transition-colors flex items-center gap-1">
                      <i className={`ri-${showPreview ? "eye-off" : "eye"}-line`} />
                      {showPreview ? "Hide list" : "Preview & edit list"}
                    </button>
                  </div>
                </div>

                {/* Preview list with remove enabled */}
                {showPreview && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-white/40 text-[10px] uppercase tracking-wide">
                        Recipient List
                        {previewTotal !== null && previewTotal > previewList.length && (
                          <span className="text-white/20 ml-1">
                            (showing {previewList.length} of {fmt(previewTotal - excludedIds.length)})
                          </span>
                        )}
                      </p>
                    </div>
                    <RecipientList
                      items={previewList}
                      onRemove={handleRemovePreview}
                      type={form.type}
                      loading={previewLoading}
                    />
                    <p className="text-white/20 text-[10px] mt-1.5 px-1">
                      <i className="ri-information-line mr-1" />
                      Showing first 500 of this segment. Removed users are excluded when the campaign is sent.
                      {previewTotal !== null && previewTotal > 500 && (
                        <span className="text-white/15"> Users beyond the first 500 are still included unless you switch to Imported List for full control.</span>
                      )}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Import section */}
          {form.recipientType === "imported_list" && (
            <div className="space-y-3">
              <div className="bg-white/3 rounded-xl border border-white/8 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <i className="ri-upload-2-line text-white/40 text-sm" />
                    <p className="text-white/60 text-sm font-semibold">
                      Import {form.type === "email" ? "Emails" : "Phone Numbers"}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => fileRef.current?.click()}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 rounded-xl text-xs text-white/60 hover:text-white hover:bg-white/10 transition-colors">
                      <i className="ri-file-text-line" /> Upload CSV
                    </button>
                    <input ref={fileRef} type="file" accept=".csv,.txt" className="hidden" onChange={handleFile} />
                  </div>
                </div>

                <textarea
                  value={importRaw}
                  onChange={e => setImportRaw(e.target.value)}
                  rows={4}
                  placeholder={form.type === "email"
                    ? "user@email.com, another@email.com\none per line or comma/semicolon separated"
                    : "08012345678, 08087654321\n+2348012345678\none per line or comma separated"
                  }
                  className="w-full bg-[#161616] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/25 focus:outline-none focus:border-[#00C896]/40 resize-none font-mono text-xs"
                />

                {importResult && (
                  <div className="flex items-center gap-3">
                    <span className="text-[#00C896] text-xs font-bold">
                      <i className="ri-checkbox-circle-line mr-1" />{importResult.valid.length} valid
                    </span>
                    {importResult.invalid.length > 0 && (
                      <span className="text-red-400 text-xs font-semibold">
                        <i className="ri-close-circle-line mr-1" />{importResult.invalid.length} invalid (skipped)
                      </span>
                    )}
                    {importResult.invalid.length > 0 && (
                      <p className="text-white/20 text-[10px] font-mono truncate flex-1">
                        {importResult.invalid.slice(0, 3).join(", ")}{importResult.invalid.length > 3 && "…"}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Editable recipient list — remove individuals */}
              {editableList.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-white/40 text-[10px] uppercase tracking-wide">
                      Recipient List — {editableList.length} recipients
                    </p>
                    <button onClick={() => setEditableList([])}
                      className="text-[10px] text-red-400/60 hover:text-red-400 transition-colors">
                      Clear all
                    </button>
                  </div>
                  <RecipientList
                    items={editableList}
                    onRemove={handleRemoveImported}
                    type={form.type}
                    loading={false}
                  />
                  <p className="text-white/20 text-[10px] mt-1.5 px-1">
                    Hover a row and click <span className="text-red-400/60">×</span> to remove that recipient before sending.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Send now toggle */}
          <div className="flex items-center justify-between p-3 bg-white/4 rounded-xl border border-white/5">
            <div>
              <p className="text-white text-sm font-semibold">Send immediately on save</p>
              <p className="text-white/30 text-xs mt-0.5">Turn off to save as a draft first</p>
            </div>
            <button onClick={() => setSendNow(s => !s)}
              className={`w-11 h-6 rounded-full transition-all relative ${sendNow ? "bg-[#00C896]" : "bg-white/10"}`}>
              <div className={`w-4 h-4 rounded-full bg-white absolute top-1 transition-all ${sendNow ? "left-6" : "left-1"}`} />
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/5 flex gap-2 flex-shrink-0">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-white/5 text-white/50 text-sm font-semibold hover:bg-white/10 transition-colors">
            Cancel
          </button>
          <button onClick={() => handleSave(false)} disabled={saving}
            className="flex-1 py-2.5 rounded-xl bg-white/5 text-white/60 text-sm font-semibold border border-white/10 hover:bg-white/10 transition-colors disabled:opacity-40">
            {saving ? "Saving…" : "Save Draft"}
          </button>
          <button onClick={() => handleSave(true)}
            disabled={saving || (totalRecipients === 0 && form.recipientType !== "imported_list") || (form.recipientType === "imported_list" && editableList.length === 0)}
            className="flex-1 py-2.5 rounded-xl bg-[#00C896] text-black text-sm font-bold hover:bg-[#00b386] transition-colors disabled:opacity-40 flex items-center justify-center gap-1.5">
            {saving
              ? <><div className="w-3.5 h-3.5 border-2 border-black/20 border-t-black rounded-full animate-spin" />Sending…</>
              : <><i className="ri-send-plane-fill" />Send Now</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Resend Modal
// ─────────────────────────────────────────────────────────────
function ResendModal({ campaign, onClose, onSaved }) {
  const [recipientType, setRecipientType] = useState(campaign.recipientType);
  const [importRaw, setImportRaw]         = useState("");
  const [importResult, setImportResult]   = useState(null);
  const [editableList, setEditableList]   = useState([]);
  const [title, setTitle]                 = useState(`${campaign.title} (Resend)`);
  const [sendNow, setSendNow]             = useState(false);
  const [saving, setSaving]               = useState(false);
  const [error, setError]                 = useState("");
  const [recipientCount, setRecipientCount] = useState(null);
  const [countLoading, setCountLoading]     = useState(false);
  const fileRef = useRef();
  const countTimer = useRef(null);

  // Live count for DB segments
  useEffect(() => {
    if (recipientType === "imported_list") { setRecipientCount(null); return; }
    clearTimeout(countTimer.current);
    countTimer.current = setTimeout(async () => {
      setCountLoading(true);
      try {
        const res = await apiClient.get(
          `/mtrade/admin/marketing/preview-count?recipientType=${recipientType}&type=${campaign.type}`
        );
        if (res.data.status) setRecipientCount(res.data.count);
      } catch { /* silent */ }
      finally { setCountLoading(false); }
    }, 400);
  }, [recipientType, campaign.type]);

  // Parse imported
  useEffect(() => {
    if (recipientType !== "imported_list" || !importRaw.trim()) {
      setImportResult(null); setEditableList([]); return;
    }
    const result = parseImportedList(importRaw, campaign.type);
    setImportResult(result);
    setEditableList(result.valid.map(r => ({ identifier: r.email || r.phone, status: "pending" })));
  }, [importRaw, campaign.type]);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const r = new FileReader();
    r.onload = (ev) => setImportRaw(ev.target.result);
    r.readAsText(file);
    e.target.value = "";
  };

  const handleSend = async () => {
    setError("");
    if (recipientType === "imported_list" && editableList.length === 0) {
      setError("No valid recipients in your list"); return;
    }
    setSaving(true);
    try {
      await apiClient.post(`/mtrade/admin/marketing/campaigns/${campaign._id}/resend`, {
        recipientType,
        title,
        sendNow,
        importedList: recipientType === "imported_list"
          ? editableList.map(r => ({ [campaign.type === "email" ? "email" : "phone"]: r.identifier }))
          : [],
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed");
    } finally { setSaving(false); }
  };

  const totalRecipients = recipientType === "imported_list" ? editableList.length : recipientCount;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
      <div className="bg-[#0f0f0f] rounded-2xl border border-white/10 w-full max-w-lg shadow-2xl flex flex-col max-h-[90vh]">

        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 flex-shrink-0">
          <div>
            <p className="text-white font-bold">Resend Campaign</p>
            <p className="text-white/40 text-xs mt-0.5">
              Resending: <span className="text-white/60">"{campaign.title}"</span>
            </p>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center hover:bg-white/10">
            <i className="ri-close-line text-white/50 text-xs" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {error && (
            <div className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-xs">
              <i className="ri-error-warning-line" /> {error}
            </div>
          )}

          {/* Message preview (read-only) */}
          <div className="bg-white/3 rounded-xl border border-white/8 p-4 space-y-2">
            <p className="text-white/40 text-[10px] uppercase tracking-wide">Original Message</p>
            {campaign.subject && (
              <p className="text-white/60 text-xs"><span className="text-white/30">Subject: </span>{campaign.subject}</p>
            )}
            <pre className="text-white/50 text-xs font-mono whitespace-pre-wrap leading-relaxed line-clamp-4">{campaign.body}</pre>
          </div>

          {/* New campaign title */}
          <div>
            <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">New Campaign Name</label>
            <input value={title} onChange={e => setTitle(e.target.value)}
              className="w-full bg-[#161616] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#00C896]/40" />
          </div>

          {/* Recipient type */}
          <div>
            <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-2">Send To</label>
            <div className="grid grid-cols-2 gap-2">
              {RECIPIENT_TYPES.map(rt => (
                <button key={rt.value} onClick={() => setRecipientType(rt.value)}
                  className={`p-3 rounded-xl border text-left transition-all ${
                    recipientType === rt.value
                      ? "border-[#00C896]/40 bg-[#00C896]/10"
                      : "border-white/8 bg-white/4 hover:bg-white/8"
                  }`}>
                  <i className={`${rt.icon} text-sm mb-1 block ${recipientType === rt.value ? "text-[#00C896]" : "text-white/40"}`} />
                  <p className={`text-xs font-semibold ${recipientType === rt.value ? "text-[#00C896]" : "text-white/60"}`}>{rt.label}</p>
                </button>
              ))}
            </div>

            {/* Count */}
            {recipientType !== "imported_list" && (
              <div className="mt-2 flex items-center gap-2">
                <i className="ri-group-line text-white/30 text-xs" />
                <p className="text-white/40 text-xs">
                  {countLoading ? "Counting…" : totalRecipients !== null
                    ? <><span className="text-[#00C896] font-bold">{fmt(totalRecipients)}</span> recipients</>
                    : "—"}
                </p>
              </div>
            )}
          </div>

          {/* Import (if imported_list) */}
          {recipientType === "imported_list" && (
            <div className="space-y-3">
              <div className="bg-white/3 rounded-xl border border-white/8 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-white/60 text-sm font-semibold">
                    Import {campaign.type === "email" ? "Emails" : "Phones"}
                  </p>
                  <button onClick={() => fileRef.current?.click()}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 rounded-xl text-xs text-white/60 hover:text-white hover:bg-white/10 transition-colors">
                    <i className="ri-file-text-line" /> Upload CSV
                  </button>
                  <input ref={fileRef} type="file" accept=".csv,.txt" className="hidden" onChange={handleFile} />
                </div>
                <textarea value={importRaw} onChange={e => setImportRaw(e.target.value)}
                  rows={4}
                  placeholder={campaign.type === "email" ? "user@email.com, …" : "08012345678, …"}
                  className="w-full bg-[#161616] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/25 focus:outline-none focus:border-[#00C896]/40 resize-none font-mono text-xs" />
                {importResult && (
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-[#00C896] font-bold"><i className="ri-checkbox-circle-line mr-1" />{importResult.valid.length} valid</span>
                    {importResult.invalid.length > 0 && <span className="text-red-400 font-semibold"><i className="ri-close-circle-line mr-1" />{importResult.invalid.length} invalid</span>}
                  </div>
                )}
              </div>

              {/* Editable list */}
              {editableList.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-white/40 text-[10px] uppercase tracking-wide">{editableList.length} recipients</p>
                    <button onClick={() => setEditableList([])} className="text-[10px] text-red-400/60 hover:text-red-400 transition-colors">Clear all</button>
                  </div>
                  <RecipientList
                    items={editableList}
                    onRemove={id => setEditableList(prev => prev.filter(r => r.identifier !== id))}
                    type={campaign.type}
                    loading={false}
                  />
                </div>
              )}
            </div>
          )}

          {/* Send now */}
          <div className="flex items-center justify-between p-3 bg-white/4 rounded-xl border border-white/5">
            <div>
              <p className="text-white text-sm font-semibold">Send immediately</p>
              <p className="text-white/30 text-xs mt-0.5">Off = saves as draft</p>
            </div>
            <button onClick={() => setSendNow(s => !s)}
              className={`w-11 h-6 rounded-full transition-all relative ${sendNow ? "bg-[#00C896]" : "bg-white/10"}`}>
              <div className={`w-4 h-4 rounded-full bg-white absolute top-1 transition-all ${sendNow ? "left-6" : "left-1"}`} />
            </button>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-white/5 flex gap-2 flex-shrink-0">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-white/5 text-white/50 text-sm font-semibold hover:bg-white/10 transition-colors">
            Cancel
          </button>
          <button onClick={handleSend}
            disabled={saving || (totalRecipients === 0 && recipientType !== "imported_list") || (recipientType === "imported_list" && editableList.length === 0)}
            className="flex-1 py-2.5 rounded-xl bg-[#00C896] text-black text-sm font-bold hover:bg-[#00b386] transition-colors disabled:opacity-40 flex items-center justify-center gap-1.5">
            {saving
              ? <><div className="w-3.5 h-3.5 border-2 border-black/20 border-t-black rounded-full animate-spin" />Creating…</>
              : <><i className="ri-repeat-line" />{sendNow ? "Resend Now" : "Save as Draft"}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Confirm Modal — replaces window.confirm
// ─────────────────────────────────────────────────────────────
function ConfirmModal({ title, message, confirmLabel = "Confirm", confirmColor = "red", onConfirm, onCancel, loading = false }) {
  const colors = {
    red:   { btn: "bg-red-600 hover:bg-red-700 text-white",  icon: "ri-delete-bin-line",     iconBg: "bg-red-500/15",  iconColor: "text-red-400"  },
    green: { btn: "bg-[#00C896] hover:bg-[#00b386] text-black", icon: "ri-send-plane-line",  iconBg: "bg-[#00C896]/15",iconColor: "text-[#00C896]"},
    amber: { btn: "bg-amber-500 hover:bg-amber-600 text-black", icon: "ri-alert-line",       iconBg: "bg-amber-500/15",iconColor: "text-amber-400"},
  };
  const c = colors[confirmColor] || colors.red;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60">
      <div className="bg-[#161616] rounded-2xl border border-white/10 w-full max-w-sm shadow-2xl overflow-hidden">
        {/* Icon + title */}
        <div className="px-6 pt-6 pb-4 text-center">
          <div className={`w-12 h-12 ${c.iconBg} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
            <i className={`${c.icon} ${c.iconColor} text-xl`} />
          </div>
          <p className="text-white font-bold text-base mb-1">{title}</p>
          <p className="text-white/40 text-sm leading-relaxed">{message}</p>
        </div>

        {/* Buttons */}
        <div className="flex gap-2 px-6 pb-6">
          <button
            onClick={onCancel}
            disabled={loading}
            className="flex-1 py-2.5 rounded-xl bg-white/5 text-white/50 text-sm font-semibold hover:bg-white/10 transition-colors disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${c.btn}`}
          >
            {loading
              ? <><div className="w-3.5 h-3.5 border-2 border-current/30 border-t-current rounded-full animate-spin" />Processing…</>
              : confirmLabel
            }
          </button>
        </div>
      </div>
    </div>
  );
}

// Works for: pending (before send), sent/failed (after send)
// ─────────────────────────────────────────────────────────────
function RecipientViewer({ campaignId, campaignStatus }) {
  const [recipients, setRecipients] = useState([]);
  const [counts, setCounts]         = useState(null);
  const [loading, setLoading]       = useState(true);
  const [page, setPage]             = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal]           = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch]         = useState("");
  const searchTimer                 = useRef(null);

  const load = useCallback(async (p = 1, sf = statusFilter, sq = search) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: 50, status: sf, search: sq }).toString();
      const res = await apiClient.get(`/mtrade/admin/marketing/campaigns/${campaignId}/recipients?${params}`);
      if (res.data.status) {
        setRecipients(p === 1 ? res.data.data : prev => [...prev, ...res.data.data]);
        setCounts(res.data.counts);
        setTotalPages(res.data.pagination.pages);
        setTotal(res.data.pagination.total);
        setPage(p);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [campaignId, statusFilter, search]);

  useEffect(() => { load(1, statusFilter, search); }, [statusFilter]);

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => load(1, statusFilter, val), 350);
  };

  const isSent = ["sent", "sending"].includes(campaignStatus);

  return (
    <div className="bg-white/4 rounded-xl border border-white/5 overflow-hidden">
      {/* Header with counts */}
      <div className="px-4 py-3 border-b border-white/5">
        <div className="flex items-center justify-between mb-2">
          <p className="text-white/40 text-[10px] uppercase tracking-wide font-bold">
            Recipients
          </p>
          {counts && (
            <span className="text-white/40 text-xs">{fmt(counts.total)} total</span>
          )}
        </div>

        {/* Status filter tabs */}
        {isSent && counts && (
          <div className="flex gap-1 mb-2">
            {[
              { value: "",        label: `All (${fmt(counts.total)})`,    color: "text-white/50"  },
              { value: "sent",    label: `✓ Sent (${fmt(counts.sent)})`,   color: "text-[#00C896]" },
              { value: "failed",  label: `✗ Failed (${fmt(counts.failed)})`, color: "text-red-400"   },
              counts.pending > 0 && { value: "pending", label: `⏳ Pending (${fmt(counts.pending)})`, color: "text-amber-400" },
            ].filter(Boolean).map(f => (
              <button key={f.value} onClick={() => setStatusFilter(f.value)}
                className={`px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-all ${
                  statusFilter === f.value ? "bg-white/10 text-white" : `${f.color} hover:bg-white/5`
                }`}>
                {f.label}
              </button>
            ))}
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-white/25 text-xs" />
          <input
            value={search}
            onChange={e => handleSearch(e.target.value)}
            placeholder="Search email or phone…"
            className="w-full bg-[#161616] border border-white/10 rounded-xl pl-8 pr-3 py-2 text-xs text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40"
          />
          {search && (
            <button onClick={() => { setSearch(""); load(1, statusFilter, ""); }}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60">
              <i className="ri-close-line text-xs" />
            </button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="overflow-y-auto max-h-72 divide-y divide-white/4">
        {loading && recipients.length === 0 ? (
          [...Array(6)].map((_, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-2.5">
              <div className="w-5 h-5 rounded-full bg-white/5 animate-pulse flex-shrink-0" />
              <div className="flex-1 h-3 bg-white/5 rounded animate-pulse" />
              <div className="w-10 h-4 bg-white/5 rounded animate-pulse" />
            </div>
          ))
        ) : recipients.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10">
            <i className="ri-inbox-line text-white/15 text-3xl mb-2" />
            <p className="text-white/25 text-xs">
              {search.trim() ? "No matches" : "No recipients yet"}
            </p>
          </div>
        ) : (
          recipients.map((r, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/3 transition-colors">
              {/* Status icon */}
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                {r.status === "sent"    && <i className="ri-checkbox-circle-fill text-[#00C896] text-sm" />}
                {r.status === "failed"  && <i className="ri-close-circle-fill text-red-400 text-sm" />}
                {r.status === "pending" && <i className="ri-time-line text-amber-400 text-sm" />}
                {!r.status              && <i className="ri-circle-line text-white/20 text-sm" />}
              </div>

              {/* Identifier */}
              <p className="flex-1 text-white/70 text-xs font-mono truncate">{r.identifier}</p>

              {/* Error message if failed */}
              {r.status === "failed" && r.error && (
                <p className="text-red-400/60 text-[10px] truncate max-w-[120px]" title={r.error}>
                  {r.error}
                </p>
              )}

              {/* Sent time */}
              {r.status === "sent" && r.sentAt && (
                <p className="text-white/20 text-[10px] flex-shrink-0">
                  {new Date(r.sentAt).toLocaleTimeString("en-NG", { hour: "2-digit", minute: "2-digit" })}
                </p>
              )}
            </div>
          ))
        )}
      </div>

      {/* Load more */}
      {page < totalPages && (
        <div className="border-t border-white/5 px-4 py-2">
          <button onClick={() => load(page + 1)} disabled={loading}
            className="w-full text-center text-xs text-[#00C896]/70 hover:text-[#00C896] transition-colors py-1 disabled:opacity-40">
            {loading ? "Loading…" : `Load more (${total - recipients.length} remaining)`}
          </button>
        </div>
      )}
    </div>
  );
}


function CampaignDetail({ id, onClose, onSend, onDelete, onResend }) {
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [sending, setSending]         = useState(false);
  const [showResend, setShowResend]   = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting]       = useState(false);

  useEffect(() => {
    apiClient.get(`/mtrade/admin/marketing/campaigns/${id}`)
      .then(res => { if (res.data.status) setData(res.data.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  const handleSend = async () => {
    setSending(true);
    try {
      await apiClient.post(`/mtrade/admin/marketing/campaigns/${id}/send`);
      onSend();
      onClose();
    } catch { /* silent */ }
    finally { setSending(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await apiClient.delete(`/mtrade/admin/marketing/campaigns/${id}`);
      onDelete();
      onClose();
    } catch { /* silent */ }
    finally { setDeleting(false); setConfirmDelete(false); }
  };

  const deliveryRate = data?.stats?.total > 0
    ? Math.round(data.stats.sent / data.stats.total * 100)
    : 0;

  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/50" onClick={onClose} />
      <div className="w-full max-w-lg bg-[#0f0f0f] flex flex-col shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 flex-shrink-0">
          {loading ? <div className="h-4 w-40 bg-white/10 rounded animate-pulse" /> : (
            <div>
              <p className="text-white font-bold">{data?.title}</p>
              <p className="text-white/40 text-xs mt-0.5 capitalize">{data?.type} · {data?.recipientType?.replace(/_/g, " ")}</p>
            </div>
          )}
          <div className="flex items-center gap-2">
            {data && <StatusPill status={data.status} />}
            <button onClick={onClose} className="w-8 h-8 rounded-xl bg-white/5 flex items-center justify-center hover:bg-white/10">
              <i className="ri-close-line text-white/60" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-6 space-y-3">{[...Array(5)].map((_, i) => <div key={i} className="h-10 bg-white/5 rounded-xl animate-pulse" />)}</div>
        ) : data ? (
          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            {/* Stats */}
            {data.status === "sent" && (
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: "Total",    value: fmt(data.stats.total),   color: "#64748B" },
                  { label: "Sent",     value: fmt(data.stats.sent),    color: "#00C896" },
                  { label: "Failed",   value: fmt(data.stats.failed),  color: "#DC2626" },
                ].map(s => (
                  <div key={s.label} className="bg-white/4 rounded-xl p-3 text-center border border-white/5">
                    <p className="font-bold text-lg" style={{ color: s.color }}>{s.value}</p>
                    <p className="text-white/30 text-[10px]">{s.label}</p>
                  </div>
                ))}
              </div>
            )}
            {data.status === "sent" && (
              <div className="bg-white/4 rounded-xl border border-white/5 p-3">
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="text-white/40">Delivery Rate</span>
                  <span className="text-white font-bold">{deliveryRate}%</span>
                </div>
                <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-[#00C896] transition-all" style={{ width: `${deliveryRate}%` }} />
                </div>
              </div>
            )}

            {/* Details */}
            <div className="bg-white/4 rounded-xl border border-white/5 overflow-hidden">
              {[
                ["Created",    fmtDate(data.createdAt)],
                ["Sent At",    fmtDate(data.sentAt)],
                data.type === "email" && ["Subject", data.subject],
                ["From",       data.fromName],
                ["Recipients", fmt(data.recipientCount)],
              ].filter(Boolean).map(([label, value]) => value && (
                <div key={label} className="flex justify-between px-4 py-3 border-b border-white/4 last:border-0 text-sm">
                  <span className="text-white/40">{label}</span>
                  <span className="text-white/70 font-medium text-right max-w-[60%]">{value}</span>
                </div>
              ))}
            </div>

            {/* Message preview */}
            <div className="bg-white/4 rounded-xl border border-white/5 overflow-hidden">
              <p className="text-white/40 text-[10px] uppercase tracking-wide px-4 py-3 border-b border-white/5">Message</p>
              <pre className="px-4 py-3 text-white/70 text-sm font-mono whitespace-pre-wrap leading-relaxed">{data.body}</pre>
            </div>

            {/* Error log */}
            {data.errorLog && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
                <p className="text-red-400 text-xs font-bold mb-1">Error</p>
                <p className="text-red-400/70 text-xs">{data.errorLog}</p>
              </div>
            )}

            {/* ── Recipient list — full paginated viewer ── */}
            {data.recipientCount > 0 || ["sent","sending","queued"].includes(data.status) ? (
              <RecipientViewer campaignId={id} campaignStatus={data.status} />
            ) : null}
          </div>
        ) : null}

        {/* Actions */}
        {data && (
          <div className="px-6 py-4 border-t border-white/5 flex gap-2 flex-shrink-0">
            {data.status === "draft" && (
              <button onClick={handleSend} disabled={sending}
                className="flex-1 py-2.5 rounded-xl bg-[#00C896] text-black text-sm font-bold hover:bg-[#00b386] transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5">
                {sending ? "Queuing…" : <><i className="ri-send-plane-fill" />Send Campaign</>}
              </button>
            )}

            {/* Resend button — shown on sent, failed, and cancelled campaigns */}
            {["sent", "failed", "cancelled", "draft"].includes(data.status) && (
              <button onClick={() => setShowResend(true)}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-white/5 text-white/60 text-sm font-bold border border-white/8 hover:bg-white/10 hover:text-white transition-colors">
                <i className="ri-repeat-line" /> Resend
              </button>
            )}

            {["draft", "failed", "sent"].includes(data.status) && (
              <button onClick={() => setConfirmDelete(true)}
                className="py-2.5 px-4 rounded-xl bg-red-500/10 text-red-400 text-sm font-bold border border-red-500/20 hover:bg-red-500/20 transition-colors">
                <i className="ri-delete-bin-line" />
              </button>
            )}
            {data.status === "sending" && (
              <p className="flex-1 text-center text-amber-400 text-sm font-semibold py-2.5">
                <i className="ri-loader-4-line animate-spin mr-1.5" />Campaign is sending…
              </p>
            )}
          </div>
        )}

        {/* Resend modal */}
        {showResend && data && (
          <ResendModal
            campaign={data}
            onClose={() => setShowResend(false)}
            onSaved={() => { onResend?.(); onClose(); }}
          />
        )}

        {/* Delete confirm modal */}
        {confirmDelete && (
          <ConfirmModal
            title="Delete Campaign"
            message={`Are you sure you want to delete "${data?.title}"? This cannot be undone.`}
            confirmLabel="Delete"
            confirmColor="red"
            loading={deleting}
            onConfirm={handleDelete}
            onCancel={() => setConfirmDelete(false)}
          />
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Main Marketing View
// ─────────────────────────────────────────────────────────────
export default function MarketingView() {
  const [campaigns, setCampaigns]   = useState([]);
  const [stats, setStats]           = useState(null);
  const [loading, setLoading]       = useState(true);
  const [page, setPage]             = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal]           = useState(0);
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showCompose, setShowCompose]   = useState(false);
  const [editCampaign, setEditCampaign] = useState(null);
  const [detailId, setDetailId]         = useState(null);
  const [connStatus, setConnStatus]     = useState(null); // null | "ok" | "error"
  const pollRef                         = useRef(null);

  const fetchCampaigns = useCallback(async (p = 1) => {
    setLoading(p === 1);
    try {
      const params = new URLSearchParams({ page: p, limit: 20, type: typeFilter, status: statusFilter }).toString();
      const res = await apiClient.get(`/mtrade/admin/marketing/campaigns?${params}`);
      if (res.data.status) {
        setCampaigns(p === 1 ? res.data.data : prev => [...prev, ...res.data.data]);
        setTotalPages(res.data.pagination.pages);
        setTotal(res.data.pagination.total);
        setPage(p);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [typeFilter, statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await apiClient.get("/mtrade/admin/marketing/stats");
      if (res.data.status) setStats(res.data.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchCampaigns(1); fetchStats(); }, [typeFilter, statusFilter]);

  // Poll for sending campaigns every 5s
  useEffect(() => {
    const hasSending = campaigns.some(c => c.status === "sending" || c.status === "queued");
    if (hasSending) {
      pollRef.current = setInterval(() => fetchCampaigns(1), 5000);
    }
    return () => clearInterval(pollRef.current);
  }, [campaigns]);

  const testConn = async () => {
    try {
      await apiClient.get("/mtrade/admin/marketing/test-connection");
      setConnStatus("ok");
    } catch { setConnStatus("error"); }
    setTimeout(() => setConnStatus(null), 4000);
  };

  const hasSending = campaigns.some(c => ["sending", "queued"].includes(c.status));

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-white">Marketing</h2>
          <p className="text-white/40 text-xs mt-0.5">
            {fmt(stats?.total)} campaigns · {fmt(stats?.totalSent)} messages sent
            {hasSending && <span className="text-amber-400 ml-2"><i className="ri-loader-4-line animate-spin mr-1" />Sending…</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* SMTP test */}
          <button onClick={testConn}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border transition-all ${
              connStatus === "ok" ? "bg-[#00C896]/15 text-[#00C896] border-[#00C896]/30"
              : connStatus === "error" ? "bg-red-500/15 text-red-400 border-red-500/30"
              : "bg-white/5 text-white/50 border-white/8 hover:text-white hover:bg-white/10"
            }`}>
            <i className={`ri-${connStatus === "ok" ? "checkbox-circle-line" : connStatus === "error" ? "error-warning-line" : "wifi-line"}`} />
            {connStatus === "ok" ? "SMTP OK" : connStatus === "error" ? "SMTP Error" : "Test SMTP"}
          </button>
          <button onClick={() => { setEditCampaign(null); setShowCompose(true); }}
            className="flex items-center gap-1.5 px-4 py-2 bg-[#00C896] text-black text-xs font-bold rounded-xl hover:bg-[#00b386] transition-colors">
            <i className="ri-add-line" /> New Campaign
          </button>
        </div>
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Total Campaigns",  value: fmt(stats.total),       icon: "ri-mail-send-line",    color: "#00C896" },
            { label: "Messages Sent",    value: fmt(stats.totalSent),   icon: "ri-send-plane-line",   color: "#0284C7" },
            { label: "Failed",           value: fmt(stats.totalFailed), icon: "ri-error-warning-line",color: "#DC2626", warn: stats.totalFailed > 0 },
            { label: "Draft",            value: fmt(stats.byStatus?.find(s => s._id === "draft")?.count || 0), icon: "ri-draft-line", color: "#64748B" },
          ].map(s => (
            <div key={s.label} className={`bg-[#161616] rounded-xl border p-4 ${s.warn ? "border-red-500/20" : "border-white/5"}`}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-2" style={{ background: s.color + "18" }}>
                <i className={`${s.icon} text-sm`} style={{ color: s.color }} />
              </div>
              <p className="text-white font-bold text-xl leading-none mb-1">{s.value}</p>
              <p className="text-white/40 text-[10px] uppercase tracking-wide">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
          className="bg-[#161616] border border-white/8 rounded-xl px-3 py-2.5 text-xs text-white/60 focus:outline-none">
          <option value="">All Channels</option>
          <option value="email">Email</option>
          <option value="sms">SMS</option>
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="bg-[#161616] border border-white/8 rounded-xl px-3 py-2.5 text-xs text-white/60 focus:outline-none">
          <option value="">All Status</option>
          {Object.entries(STATUS_STYLE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <button onClick={() => fetchCampaigns(1)} className="p-2.5 rounded-xl bg-white/5 border border-white/8 text-white/50 hover:text-white hover:bg-white/10 transition-colors">
          <i className={`ri-refresh-line text-sm ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* Campaign list */}
      <div className="bg-[#161616] border border-white/5 rounded-xl overflow-hidden">
        {/* Column headers */}
        <div className="hidden sm:grid grid-cols-[2fr_1fr_1fr_1fr_1fr] px-5 py-3 border-b border-white/5 text-[10px] font-bold text-white/25 uppercase tracking-wider">
          <span>Campaign</span><span>Channel</span><span>Recipients</span><span>Status</span><span>Date</span>
        </div>

        {loading && campaigns.length === 0 ? (
          <div className="divide-y divide-white/4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-5 py-4">
                <div className="flex-1 space-y-2"><div className="h-3 w-36 bg-white/5 rounded animate-pulse" /><div className="h-2.5 w-24 bg-white/5 rounded animate-pulse" /></div>
                <div className="h-6 w-16 bg-white/5 rounded-full animate-pulse" />
              </div>
            ))}
          </div>
        ) : campaigns.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-14 h-14 bg-white/5 rounded-xl flex items-center justify-center mb-3">
              <i className="ri-mail-send-line text-white/20 text-2xl" />
            </div>
            <p className="text-white/30 text-sm mb-1">No campaigns yet</p>
            <p className="text-white/15 text-xs">Create one to reach your users</p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-white/4">
              {campaigns.map(c => (
                <div key={c._id}
                  onClick={() => setDetailId(c._id)}
                  className="sm:grid sm:grid-cols-[2fr_1fr_1fr_1fr_1fr] flex flex-col gap-1.5 px-5 py-3.5 hover:bg-white/3 cursor-pointer transition-colors">

                  {/* Title + subject */}
                  <div className="min-w-0">
                    <p className="text-white text-sm font-semibold truncate">{c.title}</p>
                    {c.subject && <p className="text-white/30 text-xs truncate">{c.subject}</p>}
                  </div>

                  {/* Type */}
                  <div className="flex items-center">
                    <span className={`flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-lg ${
                      c.type === "email" ? "bg-[#00C896]/10 text-[#00C896]" : "bg-blue-500/10 text-blue-400"
                    }`}>
                      <i className={c.type === "email" ? "ri-mail-line" : "ri-message-2-line"} />
                      {c.type.toUpperCase()}
                    </span>
                  </div>

                  {/* Recipients + delivery */}
                  <div className="flex items-center">
                    {c.status === "sent" ? (
                      <div>
                        <p className="text-white/60 text-xs">{fmt(c.stats?.sent)} sent</p>
                        {c.stats?.failed > 0 && <p className="text-red-400 text-[10px]">{fmt(c.stats?.failed)} failed</p>}
                      </div>
                    ) : (
                      <p className="text-white/40 text-xs">{fmt(c.recipientCount)} recip.</p>
                    )}
                  </div>

                  {/* Status */}
                  <div className="flex items-center">
                    <StatusPill status={c.status} />
                  </div>

                  {/* Date */}
                  <div className="flex items-center">
                    <p className="text-white/30 text-xs">{fmtDate(c.sentAt || c.createdAt)}</p>
                  </div>
                </div>
              ))}
            </div>

            {page < totalPages && (
              <div className="px-5 py-3 border-t border-white/4">
                <button onClick={() => fetchCampaigns(page + 1)} disabled={loading}
                  className="w-full py-2.5 text-xs font-semibold text-[#00C896] hover:text-[#00b386] transition-colors disabled:opacity-50 text-center">
                  {loading ? "Loading…" : `Load more (${total - campaigns.length} remaining)`}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Compose modal */}
      {showCompose && (
        <ComposeModal
          campaign={editCampaign}
          onClose={() => { setShowCompose(false); setEditCampaign(null); }}
          onSaved={() => { fetchCampaigns(1); fetchStats(); }}
        />
      )}

      {/* Detail panel */}
      {detailId && (
        <CampaignDetail
          id={detailId}
          onClose={() => setDetailId(null)}
          onSend={() => { fetchCampaigns(1); fetchStats(); }}
          onDelete={() => { fetchCampaigns(1); fetchStats(); }}
          onResend={() => { fetchCampaigns(1); fetchStats(); }}
        />
      )}
    </div>
  );
}
